import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';

// Lazy load components
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const UserAdministration = React.lazy(() => import('./pages/UserAdministration'));
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));
const RequestPasswordReset = React.lazy(() => import('./pages/RequestPasswordReset'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ObligationDetail = React.lazy(() => import('./pages/ObligationDetail'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ObligationList = React.lazy(() => import('./components/ObligationList'));
const ObligationForm = React.lazy(() => {
  console.log('Loading ObligationForm component...');
  return import('./components/ObligationForm');
});
const CustomFieldManager = React.lazy(() => import('./components/CustomFieldManager'));
const AdminObligationsList = React.lazy(() => import('./pages/AdminObligationsList'));

// Loading component
const LoadingSpinner = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

// Protected Route component
const ProtectedRoute = ({ children, adminOnly = false }: { 
  children: React.ReactNode; 
  adminOnly?: boolean;
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  console.log('ProtectedRoute - Auth state:', {
    isAuthenticated,
    isLoading,
    hasUser: !!user,
    userRole: user?.role,
    adminOnly,
  });

  if (isLoading) {
    console.log('ProtectedRoute - Showing loading spinner due to auth loading');
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    console.log('ProtectedRoute - Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'ADMIN') {
    console.log('ProtectedRoute - Admin required but user is not admin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('ProtectedRoute - Rendering protected content');
  return (
    <Layout>
      {children}
    </Layout>
  );
};

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invitation/:token" element={<AcceptInvitation />} />
        <Route path="/reset-password" element={<RequestPasswordReset />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <UserAdministration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/custom-fields"
          element={
            <ProtectedRoute adminOnly>
              <CustomFieldManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/obligations"
          element={
            <ProtectedRoute adminOnly>
              <AdminObligationsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/obligations"
          element={
            <ProtectedRoute>
              <ObligationList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/obligations/new"
          element={
            <ProtectedRoute adminOnly>
              <ObligationForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/obligations/:id"
          element={
            <ProtectedRoute>
              <ObligationForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/obligations/:id/edit"
          element={
            <ProtectedRoute>
              <ObligationForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/obligation/:id"
          element={
            <ProtectedRoute>
              <ObligationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
